// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";

// Firebase Authentication imports
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  updateProfile, 
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserSessionPersistence
} from 'firebase/auth';

// Firebase Firestore imports
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  addDoc,
  query,
  where,
  getDocs,
  increment,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  orderBy,      
  onSnapshot
} from 'firebase/firestore';

// Firebase Storage imports
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
const analytics = getAnalytics(app);
const auth = getAuth(app);

// Set auth persistence to session only (will clear when browser is closed)
setPersistence(auth, browserSessionPersistence)
  .catch((error) => {
    console.group('Firebase Persistence Error');
    console.error("Error setting auth persistence:", error);
    console.log('Error Code:', error.code);
    console.log('Error Message:', error.message);
    console.groupEnd();
  });
  
const db = getFirestore(app);
const storage = getStorage(app);

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// Authentication helper function with improved error handling
const handleAuthError = (error) => {
  console.error('Firebase Auth Error:', error);
  let errorMessage = 'An unexpected error occurred';
  
  // Network error handling
  if (error.code === 'auth/network-request-failed') {
    errorMessage = 'Network error - please check your internet connection';
    console.warn('Network request failed. This might be caused by Content Security Policy restrictions.');
  } else {
    // Your existing error cases
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/user-disabled':
        errorMessage = 'User account has been disabled';
        break;
      case 'auth/user-not-found':
        errorMessage = 'No user found with this email';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password';
        break;
      case 'auth/email-already-in-use':
        errorMessage = 'Email already in use';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Operation not allowed';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password is too weak';
        break;
      default:
        errorMessage = error.message || 'An unexpected error occurred';
    }
  }
  
  return errorMessage;
};

// Function to attempt operations with retry for network issues
const withRetry = async (operation, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Only retry network errors
      if (error.code !== 'auth/network-request-failed') {
        throw error;
      }
      
      console.warn(`Network request failed (attempt ${attempt + 1}/${maxRetries}). Retrying in ${delay}ms...`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Increase delay for next attempt (exponential backoff)
      delay *= 2;
    }
  }
  
  // If we've exhausted all retries, throw the last error
  throw lastError;
};

export { 
  // App and Analytics
  app,
  analytics,

  // Authentication services and methods
  auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  googleProvider,
  handleAuthError,
  setPersistence,
  browserSessionPersistence,
  withRetry, // Export the retry utility

  // Firestore services and methods
  db,
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
  query,
  where,
  getDocs,
  increment,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  orderBy,
  onSnapshot,

  // Storage services
  storage,
  ref,
  uploadBytes,
  getDownloadURL
};